import debounce from 'lodash/debounce';
import TagManager from 'react-gtm-module';
import { Funnel, MagnifyingGlass } from '@phosphor-icons/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import type { SearchResultContent, SearchResultFilter } from '@tmh-website/services/search.service';
import CollapseContainer from '@tmh-website/components/shared/CollapseContainer';
import DropdownMenu from '@tmh-website/ui/DropdownMenu';
import DropdownMenuItem from '@tmh-website/ui/DropdownMenuItem';
import LoadingSpinner from '@tmh-website/ui/LoadingSpinner';
import MultiselectEntry from '@tmh-website/ui/MultiselectEntry';
import Popup from '@tmh-website/ui/Popup';
import Tag from '@tmh-website/ui/Tag';
import TextFadeOverflowScroll from '@tmh-website/components/shared/TextFadeOverflowScroll';
import { Color } from '@tmh-website/shared/colors';
import { getSearchResultDeprecated, isProjectWithSearch } from '@tmh-website/services/search.client.service';
import { ProjectContext } from '@tmh-website/shared';
import { searchResultFilterToTranslationMap, SearchResultType } from '@tmh-website/services/search.service';
import { type LocalePrefix, hasShop } from '@tmh-website/shared/const';
import { useSearchAtom } from '@tmh-website/shared/hooks';

import * as Styled from './SearchDeprecated.styles';
// TODO: this component is deprecated and should be removed in the future
import { ResultsEntryContent, ResultsEntryProduct, ResultsEntrySkeleton } from './ResultEntriesDeprecated';

/**
 * The main search component
 */
interface SearchDeprecatedProps {
  locale: LocalePrefix;
  /**
   * Enable this flag when this component is used outside of the NextJS context.
   */
  isStandaloneMode?: boolean;
}

/**
 * @deprecated This component is deprecated. Please use Search.tsx instead.
 */
const SearchDeprecated = ({ locale, isStandaloneMode }: SearchDeprecatedProps) => {
  const { project } = useContext(ProjectContext);
  const { textOpacity } = useTheme();

  if (!isProjectWithSearch(project)) {
    throw new Error(`The '${project}' project does not support search functionality!`);
  }

  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [filters, setFilters] = useState<SearchResultFilter[]>([]);
  const searchAtom = useSearchAtom();

  const hasShopAvailable = hasShop(locale, project);

  /**
   * Do not search immediately after each key stroke
   */
  const debouncedSetQuery = useCallback(debounce(setQuery, 500), []);
  useEffect(() => {
    debouncedSetQuery(searchFieldValue);
  }, [searchFieldValue]);

  /**
   * Get search data
   */
  const { data: searchResults, isFetching } = useQuery(
    ['search', locale, query, project],
    () => {
      if (query) {
        TagManager.dataLayer({ dataLayer: { event: 'search_term', search_term: query } });
      }
      return getSearchResultDeprecated(query, locale, project, hasShopAvailable);
    },
    { keepPreviousData: true }
  );

  const filteredContentResults: SearchResultContent[] = searchResults
    ? Object.keys(searchResults)
        .map(key => {
          if (
            key !== SearchResultType.Product &&
            key !== SearchResultType.Term &&
            (filters.length === 0 || filters.includes(key as SearchResultFilter))
          ) {
            return searchResults[key];
          }
          return undefined;
        })
        .flat()
        .filter(e => e !== undefined)
        .sort((a, b) => b.score - a.score)
    : [];

  const hasProductResults = !!searchResults?.[SearchResultType.Product].length;
  const hasContentResults = !!filteredContentResults.length;

  return (
    <Popup
      themeColor={Color.Neutral_0_White}
      isOpen={searchAtom.isOpen}
      onClose={searchAtom.close}
      noPadding
      maxWidth={hasShopAvailable && searchFieldValue !== '' ? '1000px' : undefined}
      isStandaloneMode={isStandaloneMode}>
      <Styled.HeaderContainer>
        <Styled.SearchField>
          {isFetching ? <LoadingSpinner size={24} /> : <MagnifyingGlass size={24} />}
          <Styled.SearchInput
            name='search'
            id='search'
            value={searchFieldValue}
            placeholder={t('global:search.inputPlaceholder') as string}
            onChange={e => setSearchFieldValue(e.currentTarget.value)}
            autoFocus
          />
          <DropdownMenu
            themeColor={Color.Neutral_200_OffWhite}
            closeOnClick={false}
            anchorEl={
              <Styled.FilterButton $isActive={filters.length > 0}>
                {filters.length > 0 && <Styled.FiltersCount>{filters.length}</Styled.FiltersCount>}
                <Funnel size={24} />
              </Styled.FilterButton>
            }>
            {Object.keys(SearchResultType)
              .filter(
                key => key !== SearchResultType.Term && (hasShopAvailable ? true : key !== SearchResultType.Product)
              )
              .map(key => {
                const type = key as SearchResultFilter;
                return (
                  <DropdownMenuItem
                    key={key}
                    onSelect={() =>
                      setFilters(current =>
                        current.includes(type) ? current.filter(f => f !== key) : [...filters, type]
                      )
                    }
                    themeColor={Color.Neutral_200_OffWhite}>
                    <MultiselectEntry
                      themeColor={Color.Neutral_200_OffWhite}
                      label={t(searchResultFilterToTranslationMap[type])}
                      value={key}
                      checked={filters.includes(type)}
                    />
                  </DropdownMenuItem>
                );
              })}
          </DropdownMenu>
        </Styled.SearchField>
        {hasShopAvailable && !!searchResults?.[SearchResultType.Term].length && (
          <TextFadeOverflowScroll fadeColor={Color.Neutral_0_White}>
            <Styled.TermsContainer>
              {searchResults[SearchResultType.Term]
                .filter(term => term.title !== query)
                .map(term => (
                  <Tag key={term.title} label={term.title} onClick={() => setSearchFieldValue(term.title)} />
                ))}
            </Styled.TermsContainer>
          </TextFadeOverflowScroll>
        )}
      </Styled.HeaderContainer>
      <CollapseContainer isOpen={searchFieldValue !== ''} displayValueWhenVisible='flex'>
        <Styled.ResultsContainer $extraPaddingTop={!hasShopAvailable}>
          {searchFieldValue.length < 3 ? (
            // Enter at least 3 characters to search
            <Styled.ErrorMessage $style='uiText' as='p' $opacity={textOpacity} $color={Color.Blue_1000_Deep}>
              {t('global:search.noEnoughCharacters')}
            </Styled.ErrorMessage>
          ) : (
            <>
              {/* Hide all result containers when no products and no pages were found */}
              {searchResults?.[SearchResultType.Product].length === 0 &&
              filteredContentResults.length === 0 &&
              !isFetching ? (
                <Styled.ErrorMessage
                  $onlyErrorMessage
                  $style='uiText'
                  as='p'
                  $opacity={textOpacity}
                  $color={Color.Blue_1000_Deep}>
                  {t('global:search.noResults')}
                </Styled.ErrorMessage>
              ) : (
                <>
                  {/* Products */}
                  {hasShopAvailable && (filters.length === 0 || filters.includes(SearchResultType.Product)) && (
                    <Styled.ResultsEntries
                      $resultsOnBoth={hasProductResults && hasContentResults}
                      $noResults={!hasProductResults}>
                      <Styled.ResultsTitle $style='uiTextBold'>{t('global:search.products')}</Styled.ResultsTitle>
                      {!isFetching &&
                        searchResults?.[SearchResultType.Product].map(entry => (
                          <ResultsEntryProduct key={entry.url} product={entry} locale={locale} />
                        ))}
                      {!isFetching && !hasProductResults && (
                        <Styled.ErrorMessage
                          $style='uiText'
                          as='p'
                          $opacity={textOpacity}
                          $color={Color.Blue_1000_Deep}>
                          {t('global:search.deprecated_noResultsProducts')}
                        </Styled.ErrorMessage>
                      )}
                      {isFetching && (
                        <>
                          <ResultsEntrySkeleton />
                          <ResultsEntrySkeleton />
                        </>
                      )}
                      <Styled.ResultsFade />
                    </Styled.ResultsEntries>
                  )}
                  {/* Pages */}
                  <Styled.ResultsEntries
                    $resultsOnBoth={hasProductResults && hasContentResults}
                    $noResults={!hasContentResults}>
                    {hasShopAvailable && (
                      <Styled.ResultsTitle $style='uiTextBold'>{t('global:search.pages')}</Styled.ResultsTitle>
                    )}
                    {!isFetching &&
                      filteredContentResults.map(entry => (
                        <ResultsEntryContent key={entry.path} locale={locale} result={entry} />
                      ))}
                    {!isFetching && !hasContentResults && (
                      <Styled.ErrorMessage $style='uiText' as='p' $opacity={textOpacity} $color={Color.Blue_1000_Deep}>
                        {t('global:search.deprecated_noResultsPages')}
                      </Styled.ErrorMessage>
                    )}
                    {isFetching && (
                      <>
                        <ResultsEntrySkeleton />
                        <ResultsEntrySkeleton />
                      </>
                    )}
                    <Styled.ResultsFade />
                  </Styled.ResultsEntries>
                </>
              )}
            </>
          )}
        </Styled.ResultsContainer>
      </CollapseContainer>
    </Popup>
  );
};

export default SearchDeprecated;
